import BTButton from '@common/components/bt-button/bt-button';
import RowStack from '@common/components/common/row-stack/RowStack';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle } from 'lucide-react';
import { getCampaignStuckMessages } from '@common/reducers/CampaignSelectors';
import { useAppDispatch, useAppSelector } from '@common/reducers/hooks/store.hook';
import { sendAllStuckMessages } from '@common/thunks/UserMessagesThunks';
import { useEffect, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { waQueryContactsAndChatsTool } from '@common/tools/whatsapp';
import CampaignActionsMenu from './campaign-actions-menu';
import CampaignMessageItemWaitingOrNotSentRow from './bulk-logs-view/campaign-message-logs/campaign-message-log-item-waiting-or-not-sent-row';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';

interface Props {
    campaignId: string;
}

export default function InProgressCampaignsStuckMessagesIndicator({ campaignId }: Props) {
    const dispatch = useAppDispatch();
    const { t } = useTranslation(namespace);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const stuckMessages = useAppSelector(state => getCampaignStuckMessages(state, campaignId));

    useEffect(() => {
        try {
            waQueryContactsAndChatsTool({})
        } catch (e) {
            console.error("[waQueryContactsAndChatsTool] error", e)
        }
    }, [dispatch]);

    if (stuckMessages.length === 0) {
        return null;
    }

    const handleSendAllMessages = () => {
        dispatch(sendAllStuckMessages(campaignId));
        setIsDialogOpen(false);
    };

    return (
        <>
            <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsDialogOpen(true)}
                className="text-yellow-600 dark:text-yellow-500 hover:text-yellow-700 dark:hover:text-yellow-400"
                title={t('campaign.stuckMessagesTitle')}
            >
                <AlertTriangle className="h-5 w-5" />
            </Button>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="max-w-2xl w-full">
                    <DialogHeader>
                        <DialogTitle>
                            <RowStack className="justify-between">
                                <span className="text-base text-[var(--color-text)] dark:text-[var(--color-text)]">
                                    {t('campaign.stuckMessagesDescription')}
                                </span>
                                <RowStack>
                                    <BTButton
                                        onClick={handleSendAllMessages}
                                        sx={{
                                            padding: "8px",
                                            borderRadius: "8px",
                                        }}
                                    >
                                        {t('campaign.sendAllMessages')}
                                    </BTButton>
                                    <CampaignActionsMenu campaignId={campaignId} />
                                </RowStack>
                            </RowStack>
                        </DialogTitle>
                    </DialogHeader>
                    <Virtuoso
                        style={{ height: '400px' }}
                        data={stuckMessages}
                        itemContent={(index, message) => (
                            <CampaignMessageItemWaitingOrNotSentRow
                                key={index}
                                message={message}
                            />
                        )}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
}
